import { useMemo } from 'react'
import { shipmentLines_shipment_shipmentLines_items_materialDetails as ShipmentOrderMaterialDetails } from '@Orders/OrderDetailsPage/Queries/__generated__/shipmentLines'

import { PROCESS_DISPLAY_CNC, PROCESSID_IM_TOOLING } from '~/lib/partsConstants'
import {
    salesOrderLines_salesOrderLines_items_material as OrderMaterialDetails,
    salesOrderLines_salesOrderLines_items_processes as ProcessDetailsType,
    salesOrderLines_salesOrderLines_items_quoteModelRevision as SalesOrderLineItemQmrType,
} from '../Queries/__generated__/salesOrderLines'
import { holes as HolesType } from '../Queries/__generated__/holes'
import { shipmentIdMapType, shipmentIdsMapType } from '../logic'

export const DEFAULT_TEXT_LENGTH = 35

export const MAX_FILE_NAME_LENGTH = 50

export const MAX_LENGTH = 20

const getConfiguredHoles = (holes: HolesType[]): number =>
    Object.keys(holes.filter(h => h.tapAnnotation)).length

const getConfigurableHoles = (holes: HolesType[]): number =>
    Object.keys(holes.filter(h => h.tapOptions)).length

export function notEmpty<TValue>(
    value: TValue | null | undefined,
): value is TValue {
    return value !== null && value !== undefined
}

export function checkEmptyValues<TValue>(
    values: Array<TValue | null | undefined> | null | undefined,
): Array<TValue> {
    const compactedValues = values?.length ? values.filter(notEmpty) : []

    return compactedValues
}

export const shouldShowThreadTags = (processName: string | null | undefined) =>
    processName ? processName.includes(PROCESS_DISPLAY_CNC) : false

export const getHolesAndTapsCount = (
    holes: HolesType[],
): { tapsCount: number; holesCount: number } => ({
    tapsCount: holes ? getConfiguredHoles(holes) : 0,
    holesCount: holes ? getConfigurableHoles(holes) : 0,
})

export const useShouldShowThreadTag = (
    processName: string | null | undefined,
) => useMemo(() => shouldShowThreadTags(processName), [processName])

export const getOrderLineDisplayName = (lineItem: {
    processName?: string | null
    materialName?: string | null
}): string => {
    const { processName, materialName } = lineItem
    const name = [materialName || 'N/A']

    if (processName) {
        name.unshift(' - ')
        name.unshift(processName)
    }

    return name.join('')
}

export const initialRevisionValues = {
    __typename: 'Revision' as const,
    id: '',
    version: '',
    modelFileName: '',
    thumbnailUri: '',
    downloadUri: '',
    sceneBundle: '',
    scene: null,
    boundingBox: null,
    revisionFolderAttachments: [
        {
            __typename: 'RevisionFolderAttachment' as const,
            id: '',
            quoteAttachment: {
                __typename: 'QuoteAttachment' as const,
                id: '',
                attachment: null,
            },
        },
    ],
}

export const getToolLifeDisplayValue = (toolLife: number) =>
    `${~~(toolLife / 1000)}k tool life`

export const getDisplayValue = ({
    attribute,
    attributeCode = null,
}: {
    attribute?: string | null
    attributeCode?: string | null
}) => `${attribute}${attributeCode ? ` ${attributeCode}` : ''}`

export const getMaterialDisplayValue = (
    materialName: string | null,
    materialGrade: string | null,
) => `${materialName}${materialGrade ? ` (${materialGrade})` : ''}`

export const getImOrderConfigDisplayValues = (
    processes?: Partial<ProcessDetailsType | null>[] | null,
    material?: OrderMaterialDetails | ShipmentOrderMaterialDetails | null,
) => {
    const primaryProcessId = processes && processes[0]?.platformProcessId
    const primaryProcessName = processes && processes[0]?.processName
    const materialName = material?.name
    const materialGrade = material?.materialGrade
    const color = material?.color
    const colorCode = material?.colorCode
    const finish = processes && processes[0]?.finish
    const finishCode = processes && processes[0]?.surfaceFinishCode
    const toolLife = processes && processes[0]?.toolLife

    let details = {
        primaryProcessDisplayName: primaryProcessName || null,
        materialDisplayName: materialName || null,
        materialGradeDisplayName: materialGrade || null,
    }

    if (primaryProcessId === PROCESSID_IM_TOOLING) {
        details = {
            ...details,
            ...{ toolLifeDisplayName: toolLife || null },
        }
    } else {
        details = {
            ...details,
            ...{
                colorCodeDisplayName: colorCode || null,
                colorDisplayName: color || null,
                finishCodeDisplayName: finishCode || null,
                finishDisplayName: finish || null,
            },
        }
    }

    return details
}

export const getShipmentCount = (
    shipmentId: string,
    shipmentIdsMap: shipmentIdsMapType,
) => {
    let count = 0

    shipmentIdsMap.forEach((idMap: shipmentIdMapType) => {
        const indexOfIdToBeMapped = parseInt(Object.keys(idMap)[0])
        const idToBeMapped = idMap[indexOfIdToBeMapped]

        if (idToBeMapped && idToBeMapped.includes(shipmentId)) {
            count = indexOfIdToBeMapped
        }
    })

    return count + 1
}

export const generateConfigInfo = (
    qmr: SalesOrderLineItemQmrType | null | undefined,
) => {
    const modelConfig = qmr?.modelConfiguration
    const processId = modelConfig?.process?.id

    // 3dp-only at the moment, feel free to change
    if (!qmr || !modelConfig || processId !== '3dp') {
        return null
    }

    const infill = modelConfig?.fdmInfillPercentageCategoryLabel
        ? `${modelConfig?.fdmInfillPercentageCategoryLabel} Infill`
        : null
    const color = modelConfig?.color?.name
    const colorCode = modelConfig?.colorCode
    const colorText = colorCode ? `${color} ${colorCode}` : color

    const values = [infill, colorText].filter(value => value)

    return values.length ? `(${values.join(', ')})` : null
}
